"use client";

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

interface SessionTokens {
  sessionToken: string | null;
  refreshToken: string | null;
}

const SessionContext = createContext<SessionTokens | undefined>(undefined);

// Refresh a bit ahead of expiry, mirroring the middleware's own skew.
const REFRESH_SKEW_MS = 60_000;
const MIN_REFRESH_DELAY_MS = 5_000;
// Recheck periodically when there's no expiry to schedule against yet
// (e.g. after a failed refresh attempt) instead of leaving the timer unset.
const RETRY_DELAY_MS = 30_000;

function redirectToLogin() {
  const { pathname } = window.location;
  // Already on a public auth page: redirecting again would nest ?next= forever.
  if (pathname === "/login" || pathname.startsWith("/auth/")) return;
  const next = `${pathname}${window.location.search}`;
  window.location.href = `/login?next=${encodeURIComponent(next)}`;
}

export function SessionProvider({
  sessionToken,
  refreshToken,
  expiresAt,
  children,
}: {
  sessionToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  children: ReactNode;
}) {
  const [tokens, setTokens] = useState<SessionTokens>({ sessionToken, refreshToken });
  const expiryRef = useRef<number | null>(expiresAt);

  const refreshSession = useCallback(async (): Promise<boolean> => {
    try {
      const res = await fetch("/api/auth/session", { cache: "no-store" });
      if (!res.ok) {
        redirectToLogin();
        return false;
      }
      const data = await res.json();
      setTokens({ sessionToken: data.sessionToken, refreshToken: data.refreshToken });
      expiryRef.current = typeof data.expiresAt === "number" ? data.expiresAt : null;
      return true;
    } catch {
      // Network hiccup: the retry loop below tries again shortly.
      return false;
    }
  }, []);

  // Proactively refresh shortly before expiry so the token used for the next
  // request is never stale, without requiring a full page reload. Always
  // reschedules itself — even after a failed attempt or an unchanged expiry —
  // so one failed refresh can't leave the session stuck stale forever.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    let cancelled = false;
    const scheduleNext = () => {
      if (cancelled) return;
      const exp = expiryRef.current;
      const delay = exp
        ? Math.max(exp * 1000 - Date.now() - REFRESH_SKEW_MS, MIN_REFRESH_DELAY_MS)
        : RETRY_DELAY_MS;
      timer = setTimeout(async () => {
        await refreshSession();
        scheduleNext();
      }, delay);
    };
    scheduleNext();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [refreshSession]);

  // Catch up after the tab/laptop was asleep past the expiry.
  useEffect(() => {
    const onFocus = () => {
      const now = Math.floor(Date.now() / 1000);
      if (expiryRef.current && now >= expiryRef.current - REFRESH_SKEW_MS / 1000) {
        refreshSession();
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refreshSession]);

  return (
    <SessionContext.Provider value={tokens}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSessionTokens(): { sessionToken: string | null; refreshToken: string | null } | undefined {
  return useContext(SessionContext);
}
